
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getCurrentSalonId, getCurrentSalonName } from "../../utils/currentSalon";
import ReportHelpCard from "../../components/reports/ReportHelpCard";
import InfoTooltip from "../../components/common/InfoTooltip";
import ConfirmModal from "../../components/modals/ConfirmModal";
import ModalCloseX from "../../components/common/ModalCloseX";
import { listCategories } from "../../api/services";
import { listStaff } from "../../api/staffs";
import { formatDateTimeInTimeZone } from "../../utils/datetime";
import "../MarketingSectionCompact.css";
import {
  createFeedbackBatch,
  createInviteBatch,
  getEmailOptInStats,
  getEmailOptInTemplate,
  previewInviteBatch,
  resetEmailOptInTemplate,
  searchEmailOptInClients,
  sendFeedbackBatch,
  sendInviteBatch,
  updateEmailOptInTemplate,
  getEmailSubscriptionSummary,
  getEmailSubscriptionTrends,
  getEmailSubscriptionReasons,
  getEmailSubscriptionPreferences,
} from "../../api/marketingEmailOptIn";
import SafeHtml from "../../components/common/SafeHtml";
import VisualTemplateBodyEditor, {
  sanitizeTemplateEditorHtml,
  templateHtmlToText,
} from "../../components/marketing/VisualTemplateBodyEditor";

const EMAIL_OPT_IN_TEMPLATE_VARIABLES = ["{{first_name}}", "{{subscribe_url}}"];

const TAB_KEYS = [
  "invite",
  "subscribed",
  "unsubscribed",
  "hard_optout",
  "feedback",
];

const SEGMENT_OPTIONS = [
  {
    value: "never_pending",
    label: "Never Asked + Pending",
    segments: ["NEVER_ASKED", "PENDING_OPT_IN"],
  },
  { value: "never_asked", label: "Never Asked", segments: ["NEVER_ASKED"] },
  { value: "pending", label: "Pending Opt-In", segments: ["PENDING_OPT_IN"] },
  { value: "unknown", label: "Unknown", segments: ["UNKNOWN"] },
];

const formatDateISO = (date, timeZone) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      return formatter.format(date);
    } catch (err) {
      // Fall back to local time.
    }
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const defaultHelpSections = [
  {
    heading: "What this page does",
    bullets: [
      "See which clients are subscribed or opted out.",
      "Invite clients who have never subscribed.",
      "Offer a coupon to lift opt-ins.",
    ],
  },
  {
    heading: "Steps",
    bullets: [
      "Review subscription stats.",
      "Invite clients who never subscribed.",
      "Offer a coupon to increase opt-ins.",
      "Respect unsubscribes and do-not-contact.",
      "Review reasons and improve content.",
    ],
  },
  {
    heading: "Best practices",
    bullets: [
      "Send 1–2 times per month.",
      "Segment lapsed clients.",
      "Keep the subject short.",
      "Always include an unsubscribe link.",
    ],
  },
];

const buildTemplatePreview = (template, context) => {
  const raw = template?.body_html || "";
  return raw.replace(/{{\s*([a-zA-Z0-9_]+)\s*}}/g, (match, token) => {
    return context[token] ?? "";
  });
};

const buildTextPreview = (template, context) => {
  const raw = template?.body_text || "";
  return raw.replace(/{{\s*([a-zA-Z0-9_]+)\s*}}/g, (match, token) => {
    return context[token] ?? "";
  });
};

export default function EmailOptIn() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const salonId = getCurrentSalonId(user);
  const salonName = getCurrentSalonName(user);
  const salonTimeZone = user?.salon_timezone || "America/Toronto";

  const [activeTab, setActiveTab] = useState(TAB_KEYS[0]);
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState("");
  const [statsLoading, setStatsLoading] = useState(false);

  const [template, setTemplate] = useState({
    subject: "",
    preheader: "",
    body_html: "",
    body_text: "",
    cta_label: "",
  });
  const [bodyTextEdited, setBodyTextEdited] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateError, setTemplateError] = useState("");

  const updateTemplateBodyHtml = useCallback(
    (nextHtml) => {
      const cleanHtml = sanitizeTemplateEditorHtml(nextHtml);
      setTemplate((prev) => ({
        ...prev,
        body_html: cleanHtml,
        body_text: bodyTextEdited ? prev.body_text : templateHtmlToText(cleanHtml),
      }));
    },
    [bodyTextEdited]
  );

  const [segmentOption, setSegmentOption] = useState("never_pending");
  const [includeUnsubscribed, setIncludeUnsubscribed] = useState(false);
  const [lastVisitStart, setLastVisitStart] = useState("");
  const [lastVisitEnd, setLastVisitEnd] = useState("");
  const [lifetimeMin, setLifetimeMin] = useState("");
  const [lifetimeMax, setLifetimeMax] = useState("");
  const [categoryIds, setCategoryIds] = useState([]);
  const [staffIds, setStaffIds] = useState([]);

  const [categories, setCategories] = useState([]);
  const [staff, setStaff] = useState([]);

  const [includeCoupon, setIncludeCoupon] = useState(true);
  const [couponType, setCouponType] = useState("PERCENT");
  const [couponValue, setCouponValue] = useState("10");
  const [couponCode, setCouponCode] = useState("");
  const [couponValidTo, setCouponValidTo] = useState("");
  const [couponLimitPerClient, setCouponLimitPerClient] = useState(true);

  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [batchId, setBatchId] = useState(null);

  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [sendError, setSendError] = useState("");
  const [draftMessage, setDraftMessage] = useState("");

  const [segmentSearch, setSegmentSearch] = useState("");
  const [segmentRows, setSegmentRows] = useState([]);
  const [segmentTotal, setSegmentTotal] = useState(0);
  const [segmentLoading, setSegmentLoading] = useState(false);
  const [segmentError, setSegmentError] = useState("");

  const [feedbackSending, setFeedbackSending] = useState(false);
  const [feedbackConfirmOpen, setFeedbackConfirmOpen] = useState(false);
  const [feedbackError, setFeedbackError] = useState("");

  const todayIso = useMemo(
    () => formatDateISO(new Date(), salonTimeZone),
    [salonTimeZone]
  );

  const segmentConfig = useMemo(
    () => SEGMENT_OPTIONS.find((option) => option.value === segmentOption),
    [segmentOption]
  );

  const previewContext = useMemo(
    () => ({
      first_name: "Jamie",
      salon_name: salonName || "Your Salon",
      salon_phone: "(555) 123-4567",
      salon_address: "123 Main St",
      booking_url: "https://booking.example.com",
      subscribe_url: "https://example.com/email/subscribe",
      unsubscribe_url: "https://example.com/email/unsubscribe",
    }),
    [salonName]
  );

  const templatePreviewHtml = useMemo(
    () => buildTemplatePreview(template, previewContext),
    [template, previewContext]
  );
  const templatePreviewText = useMemo(
    () => buildTextPreview(template, previewContext),
    [template, previewContext]
  );

  const loadStats = useCallback(async () => {
    if (!salonId) return;
    setStatsLoading(true);
    setStatsError("");
    try {
      const data = await getEmailOptInStats(salonId, token);
      setStats(data || null);
    } catch (err) {
      setStatsError(err?.response?.data?.detail || "Unable to load stats.");
    } finally {
      setStatsLoading(false);
    }
  }, [salonId, token]);

  const loadTemplate = useCallback(async () => {
    if (!salonId) return;
    setTemplateError("");
    try {
      const data = await getEmailOptInTemplate(salonId, token);
      if (data) {
        setTemplate({
          subject: data.subject || "",
          preheader: data.preheader || "",
          body_html: sanitizeTemplateEditorHtml(data.body_html || ""),
          body_text: data.body_text || "",
          cta_label: data.cta_label || "",
        });
        setBodyTextEdited(false);
      }
    } catch (err) {
      setTemplateError(
        err?.response?.data?.detail || "Unable to load template."
      );
    }
  }, [salonId, token]);

  const loadFilters = useCallback(async () => {
    try {
      const [categoryData, staffData] = await Promise.all([
        listCategories({ kind: "service" }, token),
        listStaff({ salon_id: salonId }, token),
      ]);
      setCategories(Array.isArray(categoryData) ? categoryData : []);
      setStaff(Array.isArray(staffData) ? staffData : []);
    } catch (err) {
      setCategories([]);
      setStaff([]);
    }
  }, [salonId, token]);

  useEffect(() => {
    loadStats();
    loadTemplate();
    loadFilters();
  }, [loadStats, loadTemplate, loadFilters]);

  useEffect(() => {
    if (!draftMessage) return undefined;
    const timeout = setTimeout(() => setDraftMessage(""), 2400);
    return () => clearTimeout(timeout);
  }, [draftMessage]);

  const buildAudienceFilters = () => {
    const filters = {
      segments: segmentConfig?.segments || ["NEVER_ASKED", "PENDING_OPT_IN"],
      include_unsubscribed: includeUnsubscribed,
    };
    if (lastVisitStart || lastVisitEnd) {
      filters.last_visit_between = {
        start: lastVisitStart || null,
        end: lastVisitEnd || null,
      };
    }
    if (lifetimeMin) filters.lifetime_spend_min = lifetimeMin;
    if (lifetimeMax) filters.lifetime_spend_max = lifetimeMax;
    if (categoryIds.length > 0) filters.service_category_ids = categoryIds;
    if (staffIds.length > 0) filters.technician_ids = staffIds;
    if (segmentSearch) filters.search = segmentSearch;
    return filters;
  };

  const buildCouponOffer = () => {
    if (!includeCoupon) return { enabled: false };
    return {
      enabled: true,
      type: couponType,
      value: Number(couponValue) || 0,
      code: couponCode || `OPTIN${couponValue || "10"}`,
      valid_to: couponValidTo || null,
      max_redemptions_per_client: couponLimitPerClient ? 1 : null,
      applies_to: "ALL",
    };
  };

  const handlePreviewRecipients = async () => {
    if (!salonId) return;
    setPreviewLoading(true);
    setPreviewError("");
    try {
      const batch = await createInviteBatch(
        {
          salon_id: salonId,
          audience_filters_json: buildAudienceFilters(),
          coupon_offer_json: buildCouponOffer(),
        },
        token
      );
      setBatchId(batch?.id || null);
      const preview = await previewInviteBatch(batch.id, token);
      setPreviewData(preview || null);
    } catch (err) {
      setPreviewError(
        err?.response?.data?.detail || "Unable to preview recipients."
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!salonId) return;
    setTemplateSaving(true);
    setTemplateError("");
    try {
      await updateEmailOptInTemplate(
        salonId,
        {
          subject: template.subject,
          preheader: template.preheader,
          body_html: sanitizeTemplateEditorHtml(template.body_html),
          body_text: template.body_text,
          cta_label: template.cta_label,
        },
        token
      );
      const batch = await createInviteBatch(
        {
          salon_id: salonId,
          audience_filters_json: buildAudienceFilters(),
          coupon_offer_json: buildCouponOffer(),
        },
        token
      );
      setBatchId(batch?.id || null);
      setDraftMessage("Draft saved.");
    } catch (err) {
      setTemplateError(
        err?.response?.data?.detail || "Unable to save draft."
      );
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleSendInvite = async () => {
    if (!salonId) return;
    setSendLoading(true);
    setSendError("");
    try {
      let currentBatchId = batchId;
      if (!currentBatchId) {
        const batch = await createInviteBatch(
          {
            salon_id: salonId,
            audience_filters_json: buildAudienceFilters(),
            coupon_offer_json: buildCouponOffer(),
          },
          token
        );
        currentBatchId = batch?.id;
        setBatchId(currentBatchId || null);
      }
      if (currentBatchId) {
        await sendInviteBatch(currentBatchId, token);
        setDraftMessage("Invitations queued for sending.");
        loadStats();
      }
      setSendConfirmOpen(false);
    } catch (err) {
      setSendError(
        err?.response?.data?.detail || "Unable to send invitations."
      );
    } finally {
      setSendLoading(false);
    }
  };

  const loadSegmentRows = useCallback(
    async (segmentKey) => {
      if (!salonId) return;
      setSegmentLoading(true);
      setSegmentError("");
      try {
        const payload = {
          salon_id: salonId,
          segment: segmentKey,
          filters: { search: segmentSearch },
          page: 1,
          page_size: 50,
        };
        const data = await searchEmailOptInClients(payload, token);
        setSegmentRows(data?.rows || []);
        setSegmentTotal(data?.total || 0);
      } catch (err) {
        setSegmentError(
          err?.response?.data?.detail || "Unable to load clients."
        );
      } finally {
        setSegmentLoading(false);
      }
    },
    [salonId, token, segmentSearch]
  );

  useEffect(() => {
    if (activeTab === "subscribed") {
      loadSegmentRows("SUBSCRIBED");
    } else if (activeTab === "unsubscribed") {
      loadSegmentRows("UNSUBSCRIBED");
    } else if (activeTab === "hard_optout") {
      loadSegmentRows("HARD_OPTOUT");
    }
  }, [activeTab, loadSegmentRows]);

  const handleResetTemplate = async () => {
    if (!salonId) return;
    setTemplateSaving(true);
    setTemplateError("");
    try {
      const data = await resetEmailOptInTemplate(salonId, token);
      setTemplate({
        subject: data?.template?.subject || "",
        preheader: data?.template?.preheader || "",
        body_html: sanitizeTemplateEditorHtml(data?.template?.body_html || ""),
        body_text: data?.template?.body_text || "",
        cta_label: data?.template?.cta_label || "",
      });
      setBodyTextEdited(false);
      setDraftMessage("Template reset.");
    } catch (err) {
      setTemplateError(
        err?.response?.data?.detail || "Unable to reset template."
      );
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleRequestFeedback = async () => {
    if (!salonId) return;
    setFeedbackSending(true);
    setFeedbackError("");
    try {
      const batch = await createFeedbackBatch({ salon_id: salonId }, token);
      if (batch?.id) {
        await sendFeedbackBatch(batch.id, token);
        setDraftMessage("Feedback requests queued.");
      }
      setFeedbackConfirmOpen(false);
    } catch (err) {
      setFeedbackError(
        err?.response?.data?.detail || "Unable to request feedback."
      );
    } finally {
      setFeedbackSending(false);
    }
  };

  const statCards = [
    { label: "Clients with email", value: stats?.total_clients_with_email },
    { label: "Subscribed", value: stats?.subscribed_count },
    { label: "Unsubscribed", value: stats?.unsubscribed_count },
    { label: "Never Asked", value: stats?.never_asked_count },
    { label: "Pending Opt-In", value: stats?.pending_optin_count },
    { label: "Unknown", value: stats?.unknown_count },
    { label: "Hard Opt-Out", value: stats?.hard_optout_count },
  ];

  const segmentRowsEmpty = segmentRows.length === 0;
  const segmentSummary = segmentTotal ? `${segmentTotal} clients` : "No clients";

  const openUnsubscribePreview = () => {
    if (!salonId) return;
    const params = new URLSearchParams({
      salon_id: String(salonId),
      category: "all_optional",
    });
    window.open(`/email/unsubscribe/preview?${params.toString()}`, "_blank", "noopener");
  };

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/manager/schedule");
  };

  return (
    <div className="marketingSectionShell">
      <div className="marketingSectionWide marketing-section-wide marketing-section-compact text-sm md:text-base">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 relative">
            <h1 className="text-xl md:text-2xl font-semibold text-white">
              Email Opt-In
            </h1>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>

          <div className="px-6 py-6 space-y-6">
            <ReportHelpCard
              title="Help Guide"
              subtitle="Invite clients to subscribe and track preferences."
              sections={defaultHelpSections}
              storageKey="email-optin-help"
              showLabel="Show Help"
              hideLabel="Hide"
            />

            {statsError && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                {statsError}
              </div>
            )}

            {draftMessage && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700">
                {draftMessage}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-7 gap-3">
              {statCards.map((card) => (
                <div
                  key={card.label}
                  className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                >
                  <div className="text-xs font-semibold uppercase text-slate-500">
                    {card.label}
                  </div>
                  <div className="text-lg font-semibold text-slate-900">
                    {statsLoading ? "…" : card.value || 0}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              {[
                { key: "invite", label: "Invite to Subscribe" },
                { key: "subscribed", label: "Subscribed" },
                { key: "unsubscribed", label: "Unsubscribed" },
                { key: "hard_optout", label: "Hard Opt-Out" },
                { key: "feedback", label: "Feedback Insights" },
              ].map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`rounded-full px-4 py-2 text-xs font-semibold ${
                    activeTab === tab.key
                      ? "bg-slate-900 text-white"
                      : "border border-slate-300 text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === "invite" && (
              <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-6">
                <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-4">
                  <div className="text-base md:text-lg font-semibold text-slate-900">
                    Audience Builder
                  </div>
                  <div className="space-y-3">
                    <label className="text-xs font-semibold text-slate-500">
                      Segment
                    </label>
                    <select
                      value={segmentOption}
                      onChange={(e) => setSegmentOption(e.target.value)}
                      className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                    >
                      {SEGMENT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={includeUnsubscribed}
                        onChange={(e) => setIncludeUnsubscribed(e.target.checked)}
                        className="h-4 w-4 rounded border-slate-300"
                      />
                      Include Unsubscribed
                    </label>
                    <InfoTooltip
                      text="Only re-invite if you are compliant with local rules."
                      label="Re-invite warning"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-slate-500">
                        Last visit start
                      </label>
                      <input
                        type="date"
                        value={lastVisitStart}
                        onChange={(e) => setLastVisitStart(e.target.value)}
                        className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-500">
                        Last visit end
                      </label>
                      <input
                        type="date"
                        value={lastVisitEnd}
                        onChange={(e) => setLastVisitEnd(e.target.value)}
                        className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-500">
                        Lifetime spend min
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={lifetimeMin}
                        onChange={(e) => setLifetimeMin(e.target.value)}
                        className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-500">
                        Lifetime spend max
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={lifetimeMax}
                        onChange={(e) => setLifetimeMax(e.target.value)}
                        className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-slate-500">
                        Service categories
                      </label>
                      <select
                        multiple
                        value={categoryIds}
                        onChange={(e) =>
                          setCategoryIds(
                            Array.from(e.target.selectedOptions).map((opt) =>
                              Number(opt.value)
                            )
                          )
                        }
                        className="mt-1 h-32 w-full rounded-lg border border-slate-300 px-3 text-sm"
                      >
                        {categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-500">
                        Technician
                      </label>
                      <select
                        multiple
                        value={staffIds}
                        onChange={(e) =>
                          setStaffIds(
                            Array.from(e.target.selectedOptions).map((opt) =>
                              Number(opt.value)
                            )
                          )
                        }
                        className="mt-1 h-32 w-full rounded-lg border border-slate-300 px-3 text-sm"
                      >
                        {staff.map((member) => (
                          <option key={member.id} value={member.id}>
                            {member.display_name || member.full_name || member.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={handlePreviewRecipients}
                      className="rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-800"
                      disabled={previewLoading}
                    >
                      {previewLoading ? "Previewing..." : "Preview Recipients"}
                    </button>
                    {previewData && (
                      <div className="text-xs text-slate-600">
                        {previewData.total || 0} recipients found
                      </div>
                    )}
                  </div>
                  {previewError && (
                    <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-700">
                      {previewError}
                    </div>
                  )}
                  {previewData && (
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                      <div className="text-xs font-semibold text-slate-500 uppercase">
                        Sample recipients
                      </div>
                      <div className="mt-2 space-y-2">
                        {(previewData.sample_recipients || []).map((rec) => (
                          <div
                            key={rec.client_id}
                            className="flex items-center justify-between text-xs text-slate-700"
                          >
                            <div>
                              <div className="font-semibold text-slate-900">
                                {rec.full_name}
                              </div>
                              <div>{rec.email}</div>
                            </div>
                            <div className="text-slate-500">
                              {rec.last_visit_date || "--"}
                            </div>
                          </div>
                        ))}
                        {(previewData.sample_recipients || []).length === 0 && (
                          <div className="text-xs text-slate-500">
                            No preview recipients.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-6">
                  <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-base md:text-lg font-semibold text-slate-900">
                        Coupon Offer
                      </div>
                      <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={includeCoupon}
                          onChange={(e) => setIncludeCoupon(e.target.checked)}
                          className="h-4 w-4 rounded border-slate-300"
                        />
                        Include coupon on subscribe
                      </label>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-semibold text-slate-500">
                          Type
                        </label>
                        <select
                          value={couponType}
                          onChange={(e) => setCouponType(e.target.value)}
                          className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                          disabled={!includeCoupon}
                        >
                          <option value="PERCENT">Percent</option>
                          <option value="FIXED">Fixed</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-500">
                          Value
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={couponValue}
                          onChange={(e) => setCouponValue(e.target.value)}
                          className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                          disabled={!includeCoupon}
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-500">
                          Valid until
                        </label>
                        <input
                          type="date"
                          value={couponValidTo}
                          onChange={(e) => setCouponValidTo(e.target.value)}
                          className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                          disabled={!includeCoupon}
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-500">
                          Code
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                            className="mt-1 h-10 flex-1 rounded-lg border border-slate-300 px-3 text-sm"
                            disabled={!includeCoupon}
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setCouponCode(`OPTIN${couponValue || "10"}`)
                            }
                            className="mt-1 rounded-full border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700"
                            disabled={!includeCoupon}
                          >
                            Auto
                          </button>
                        </div>
                      </div>
                    </div>
                    <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={couponLimitPerClient}
                        onChange={(e) => setCouponLimitPerClient(e.target.checked)}
                        className="h-4 w-4 rounded border-slate-300"
                        disabled={!includeCoupon}
                      />
                      Limit 1 per client
                    </label>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="text-base md:text-lg font-semibold text-slate-900">
                        Email Template
                      </div>
                      <button
                        type="button"
                        onClick={openUnsubscribePreview}
                        className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700"
                      >
                        Preview Unsubscribe Page
                      </button>
                      <button
                        type="button"
                        onClick={handleResetTemplate}
                        className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700"
                        disabled={templateSaving}
                      >
                        Reset
                      </button>
                    </div>
                    {templateError && (
                      <div className="rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                        {templateError}
                      </div>
                    )}
                    <div>
                      <label className="text-xs font-semibold text-slate-500">
                        Subject
                      </label>
                      <input
                        type="text"
                        value={template.subject}
                        onChange={(e) =>
                          setTemplate((prev) => ({
                            ...prev,
                            subject: e.target.value,
                          }))
                        }
                        className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-500">
                        Preheader
                      </label>
                      <input
                        type="text"
                        value={template.preheader}
                        onChange={(e) =>
                          setTemplate((prev) => ({
                            ...prev,
                            preheader: e.target.value,
                          }))
                        }
                        className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-500">
                        Message
                      </label>
                      <div className="mt-1">
                        <VisualTemplateBodyEditor
                          value={template.body_html}
                          onChange={updateTemplateBodyHtml}
                          variables={EMAIL_OPT_IN_TEMPLATE_VARIABLES}
                        />
                      </div>
                    </div>

                    <details className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                      <summary className="cursor-pointer text-xs font-semibold text-slate-600">
                        Advanced HTML
                      </summary>
                      <textarea
                        rows="5"
                        value={template.body_html}
                        onChange={(e) => updateTemplateBodyHtml(e.target.value)}
                        className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-mono"
                      />
                      <div className="mt-2 text-xs text-slate-500">
                        Use tokens like {`{{first_name}}`} and {`{{subscribe_url}}`}.
                      </div>
                    </details>
                    <div>
                      <label className="text-xs font-semibold text-slate-500">
                        Body Text (optional)
                      </label>
                      <textarea
                        rows="4"
                        value={template.body_text}
                        onChange={(e) => {
                          setBodyTextEdited(true);
                          setTemplate((prev) => ({
                            ...prev,
                            body_text: e.target.value,
                          }));
                        }}
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                      />
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                      <div className="text-xs font-semibold text-slate-500 uppercase">
                        Live preview
                      </div>
                      <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-700">
                        <div className="text-xs text-slate-500">
                          {template.preheader}
                        </div>
                        <SafeHtml
                          className="mt-2 prose prose-sm max-w-none"
                           html={templatePreviewHtml} />
                        <div className="mt-3 text-xs text-slate-500 whitespace-pre-line">
                          {templatePreviewText}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={handleSaveDraft}
                      className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700"
                      disabled={templateSaving}
                    >
                      {templateSaving ? "Saving..." : "Save Draft"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setSendConfirmOpen(true)}
                      className="rounded-full bg-rose-500 px-4 py-2 text-xs font-semibold text-white hover:bg-rose-600"
                      disabled={templateSaving}
                    >
                      Send Invitation
                    </button>
                  </div>
                  {sendError && (
                    <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-700">
                      {sendError}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === "subscribed" && (
              <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-base font-semibold text-slate-900">
                      Subscribed Clients
                    </div>
                    <div className="text-xs text-slate-500">{segmentSummary}</div>
                  </div>
                  <input
                    type="text"
                    placeholder="Search by name or email"
                    value={segmentSearch}
                    onChange={(e) => setSegmentSearch(e.target.value)}
                    className="h-9 w-full max-w-xs rounded-lg border border-slate-300 px-3 text-sm"
                  />
                </div>
                {segmentError && (
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-700">
                    {segmentError}
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-left">Client</th>
                        <th className="px-3 py-2 text-left">Email</th>
                        <th className="px-3 py-2 text-left">Subscribed</th>
                        <th className="px-3 py-2 text-left">Preferences</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {segmentRows.map((row) => (
                        <tr key={row.client_id}>
                          <td className="px-3 py-2 text-slate-700">
                            {row.name}
                          </td>
                          <td className="px-3 py-2 text-slate-600">
                            {row.email}
                          </td>
                          <td className="px-3 py-2 text-slate-600">
                            {row.invited_at
                              ? formatDateTimeInTimeZone(
                                  row.invited_at,
                                  salonTimeZone
                                )
                              : "--"}
                          </td>
                          <td className="px-3 py-2 text-slate-600">
                            {row.preferences_summary || "--"}
                          </td>
                        </tr>
                      ))}
                      {!segmentLoading && segmentRowsEmpty && (
                        <tr>
                          <td
                            colSpan="4"
                            className="px-3 py-6 text-center text-xs text-slate-500"
                          >
                            No subscribed clients found.
                          </td>
                        </tr>
                      )}
                      {segmentLoading && (
                        <tr>
                          <td
                            colSpan="4"
                            className="px-3 py-6 text-center text-xs text-slate-500"
                          >
                            Loading...
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === "unsubscribed" && (
              <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-base font-semibold text-slate-900">
                      Unsubscribed Clients
                    </div>
                    <div className="text-xs text-slate-500">{segmentSummary}</div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="text"
                      placeholder="Search by name or email"
                      value={segmentSearch}
                      onChange={(e) => setSegmentSearch(e.target.value)}
                      className="h-9 w-full max-w-xs rounded-lg border border-slate-300 px-3 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setFeedbackConfirmOpen(true)}
                      className="rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white"
                    >
                      Request Feedback
                    </button>
                  </div>
                </div>
                {feedbackError && (
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-700">
                    {feedbackError}
                  </div>
                )}
                {segmentError && (
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-700">
                    {segmentError}
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-left">Client</th>
                        <th className="px-3 py-2 text-left">Email</th>
                        <th className="px-3 py-2 text-left">Unsubscribed</th>
                        <th className="px-3 py-2 text-left">Reason</th>
                        <th className="px-3 py-2 text-left">Preferences</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {segmentRows.map((row) => (
                        <tr key={row.client_id}>
                          <td className="px-3 py-2 text-slate-700">
                            {row.name}
                          </td>
                          <td className="px-3 py-2 text-slate-600">
                            {row.email}
                          </td>
                          <td className="px-3 py-2 text-slate-600">
                            {row.unsubscribed_at
                              ? formatDateTimeInTimeZone(
                                  row.unsubscribed_at,
                                  salonTimeZone
                                )
                              : "--"}
                          </td>
                          <td className="px-3 py-2 text-slate-600">
                            {row.unsubscribe_reason || "--"}
                          </td>
                          <td className="px-3 py-2 text-slate-600">
                            {row.preferences_summary || "--"}
                          </td>
                        </tr>
                      ))}
                      {!segmentLoading && segmentRowsEmpty && (
                        <tr>
                          <td
                            colSpan="5"
                            className="px-3 py-6 text-center text-xs text-slate-500"
                          >
                            No unsubscribed clients found.
                          </td>
                        </tr>
                      )}
                      {segmentLoading && (
                        <tr>
                          <td
                            colSpan="5"
                            className="px-3 py-6 text-center text-xs text-slate-500"
                          >
                            Loading...
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === "hard_optout" && (
              <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-4">
                <div className="flex items-center gap-2">
                  <div className="text-base font-semibold text-slate-900">
                    Hard Opt-Out
                  </div>
                  <InfoTooltip text="Hard opt-out means do-not-contact for marketing." />
                </div>
                <div className="text-xs text-slate-500">{segmentSummary}</div>
                {segmentError && (
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-700">
                    {segmentError}
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-left">Client</th>
                        <th className="px-3 py-2 text-left">Email</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {segmentRows.map((row) => (
                        <tr key={row.client_id}>
                          <td className="px-3 py-2 text-slate-700">
                            {row.name}
                          </td>
                          <td className="px-3 py-2 text-slate-600">
                            {row.email}
                          </td>
                        </tr>
                      ))}
                      {!segmentLoading && segmentRowsEmpty && (
                        <tr>
                          <td
                            colSpan="2"
                            className="px-3 py-6 text-center text-xs text-slate-500"
                          >
                            No hard opt-out clients found.
                          </td>
                        </tr>
                      )}
                      {segmentLoading && (
                        <tr>
                          <td
                            colSpan="2"
                            className="px-3 py-6 text-center text-xs text-slate-500"
                          >
                            Loading...
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === "feedback" && (
              <EmailOptInFeedbackInsights
                salonId={salonId}
                salonTimeZone={salonTimeZone}
                token={token}
              />
            )}
          </div>
        </div>
      </div>

      <ConfirmModal
        open={sendConfirmOpen}
        title="Send invitations?"
        message="Send the opt-in invitation to selected clients now?"
        confirmText={sendLoading ? "Sending..." : "Send Invitations"}
        cancelText="Cancel"
        onClose={() => setSendConfirmOpen(false)}
        onConfirm={handleSendInvite}
        isBusy={sendLoading}
      />
      <ConfirmModal
        open={feedbackConfirmOpen}
        title="Request feedback?"
        message="Send a feedback request to unsubscribed clients?"
        confirmText={feedbackSending ? "Sending..." : "Send Requests"}
        cancelText="Cancel"
        onClose={() => setFeedbackConfirmOpen(false)}
        onConfirm={handleRequestFeedback}
        isBusy={feedbackSending}
      />
    </div>
  );
}

function EmailOptInFeedbackInsights({ salonId, salonTimeZone, token }) {
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [preferences, setPreferences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const todayIso = useMemo(
    () => formatDateISO(new Date(), salonTimeZone),
    [salonTimeZone]
  );
  const ninetyDaysAgo = useMemo(() => {
    const base = new Date();
    base.setDate(base.getDate() - 90);
    return formatDateISO(base, salonTimeZone);
  }, [salonTimeZone]);

  useEffect(() => {
    if (!salonId) return;
    const run = async () => {
      setLoading(true);
      setError("");
      try {
        const payload = {
          salon_id: salonId,
          start_date: ninetyDaysAgo,
          end_date: todayIso,
        };
        const [
          summaryData,
          trendData,
          reasonData,
          preferenceData,
        ] = await Promise.all([
          getEmailSubscriptionSummary(payload, token),
          getEmailSubscriptionTrends(payload, token),
          getEmailSubscriptionReasons(payload, token),
          getEmailSubscriptionPreferences(payload, token),
        ]);
        setSummary(summaryData || null);
        setTrends(trendData?.points || []);
        setReasons(reasonData?.rows || []);
        setPreferences(preferenceData?.rows || []);
      } catch (err) {
        setError(err?.response?.data?.detail || "Unable to load insights.");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [salonId, token, ninetyDaysAgo, todayIso]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-4">
      <div className="text-base font-semibold text-slate-900">
        Feedback Insights (Last 90 Days)
      </div>
      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-700">
          {error}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
          <div className="text-xs uppercase text-slate-500 font-semibold">
            New subscribers
          </div>
          <div className="text-lg font-semibold text-slate-900">
            {loading ? "…" : summary?.new_subscribers || 0}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
          <div className="text-xs uppercase text-slate-500 font-semibold">
            Unsubscribes
          </div>
          <div className="text-lg font-semibold text-slate-900">
            {loading ? "…" : summary?.unsubscribes || 0}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
          <div className="text-xs uppercase text-slate-500 font-semibold">
            Top reason
          </div>
          <div className="text-sm font-semibold text-slate-900">
            {loading ? "…" : summary?.top_unsubscribe_reason || "—"}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-3">
          <div className="text-sm font-semibold text-slate-900">
            Top unsubscribe reasons
          </div>
          <ul className="mt-2 space-y-2 text-sm text-slate-700">
            {reasons.map((row) => (
              <li key={row.reason} className="flex items-center justify-between">
                <span>{row.reason}</span>
                <span className="text-slate-500">{row.count}</span>
              </li>
            ))}
            {reasons.length === 0 && (
              <li className="text-xs text-slate-500">No reasons yet.</li>
            )}
          </ul>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-3">
          <div className="text-sm font-semibold text-slate-900">
            Preferred content
          </div>
          <ul className="mt-2 space-y-2 text-sm text-slate-700">
            {preferences.map((row) => (
              <li
                key={row.preference}
                className="flex items-center justify-between"
              >
                <span>{row.preference.replace(/_/g, " ")}</span>
                <span className="text-slate-500">{row.count}</span>
              </li>
            ))}
            {preferences.length === 0 && (
              <li className="text-xs text-slate-500">
                No preferences captured yet.
              </li>
            )}
          </ul>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-3">
        <div className="text-sm font-semibold text-slate-900">
          Unsubscribes trend
        </div>
        <div className="mt-3 space-y-2">
          {trends.map((point) => (
            <div key={point.date} className="flex items-center gap-3">
              <div className="w-24 text-xs text-slate-500">{point.date}</div>
              <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-2 bg-rose-400"
                  style={{ width: `${Math.min(point.unsubscribed * 10, 100)}%` }}
                />
              </div>
              <div className="text-xs text-slate-600">
                {point.unsubscribed}
              </div>
            </div>
          ))}
          {trends.length === 0 && (
            <div className="text-xs text-slate-500">No trend data.</div>
          )}
        </div>
      </div>
    </div>
  );
}
